/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import {
    isAdvertisementEntry,
    isAudiobookEntry,
    type Player,
    type PlaylistController,
    withUserSkip,
} from '@amzn/bf-playback'
import { promptSignInIfAnonymous } from '@/auth/promptSignInIfAnonymous'
import { guardedBackStep, guardedRestart } from '@/playback/skipGuard'

const RESTART_THRESHOLD_SECONDS = 3

/**
 * The transport "previous" gesture shared by the previous button, swipe, keyboard, media keys and
 * the lock screen (hornet's `PlaybackController.back`): within the first 3 s of the current entry
 * step back to the previous one when allowed, otherwise restart it when scrubbing back is allowed.
 * Signed out (a preview) every press opens sign-in instead, whatever the position.
 * Audiobooks apply the rule, ungated, to the current chapter (`seekTo` is chapter-relative there).
 * Decides on the content clock and no-ops during an ad break or a queue ad, matching the previous
 * button hornet disables for either.
 */
export async function previousOrRestart(
    player: Player,
    playlistController: PlaylistController
): Promise<void> {
    if (player.ads.adState$.value != null) return
    if (isAdvertisementEntry(playlistController.current.value)) return
    if (promptSignInIfAnonymous()) return
    if (isAudiobookEntry(playlistController.current.value)) {
        if (player.sections.getPositionInSection() >= RESTART_THRESHOLD_SECONDS) {
            await player.seekTo(0)
            return
        }
        await withUserSkip('previous', () => player.sections.previousSection())
        return
    }
    if (player.contentTime$.value < RESTART_THRESHOLD_SECONDS) {
        const step = guardedBackStep(playlistController)
        if (step == null) return
        if (step === 'previous') {
            const previous = await withUserSkip('previous', () => playlistController.skipPrevious())
            if (previous != null) return
        }
    }
    if (guardedRestart(playlistController)) await player.seekTo(0, { reason: 'restart' })
}
